import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@/lib/logger';

const DEFAULT_TIMEOUT = 2000;

type UseClipboardReturnType = {
  /**
   * The state indicating whether the text has been copied.
   * If a string is provided, it will be used as the identifier for the copied state.
   */
  copied: string | boolean;
  /**
   * Function to copy text to the clipboard using the modern clipboard API.
   * Falls back to the fallback function if the modern API fails.
   *
   * @param {string} text - The text to be copied.
   * @param {string} [id] - Optional identifier to set the copied state.
   * @returns {Promise<Object>} - A promise that resolves to an object containing:
   *  - `success` (boolean): Whether the copy operation was successful.
   *  - `error` (Error | undefined): The error object if the copy operation failed.
   */
  copy: (
    text: string,
    id?: string
  ) => Promise<{ success: boolean; error?: Error }>;
};

/**
 * Custom hook to copy text to the clipboard.
 *
 * @returns {UseClipboardReturnType} - An object containing the copied state and the copy function.
 */
export const useClipboard = (): UseClipboardReturnType => {
  const [copied, setCopied] = useState<string | boolean>(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => clearTimeout(resetTimeoutRef.current);
  }, []);

  const scheduleReset = useCallback(() => {
    clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(
      () => setCopied(false),
      DEFAULT_TIMEOUT
    );
  }, []);

  // Fallback function for older browsers
  const fallback = useCallback(
    (text: string, id?: string) => {
      let textArea: HTMLTextAreaElement | undefined;
      const previouslyFocused = document.activeElement;

      try {
        // Textarea to copy the text to the clipboard
        textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'absolute';
        textArea.style.left = '-99999px';

        document.body.appendChild(textArea);
        textArea.select();

        const success = document.execCommand('copy');

        if (success) {
          setCopied(id || true);
          scheduleReset();
          return { success: true };
        }

        return {
          success: false,
          error: new Error('execCommand returned false'),
        };
      } catch (e) {
        const error =
          e instanceof Error ? e : new Error('Fallback copy failed');
        logger.warn('Clipboard fallback copy failed', { error });
        return { success: false, error };
      } finally {
        textArea?.remove();
        if (
          previouslyFocused instanceof HTMLElement &&
          document.contains(previouslyFocused)
        ) {
          previouslyFocused.focus();
        }
      }
    },
    [scheduleReset]
  );

  const copy = useCallback(
    async (text: string, id?: string) => {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);

          setCopied(id || true);
          scheduleReset();

          return { success: true };
        } catch {
          // If modern method fails, try fallback
          return fallback(text, id);
        }
      }
      return fallback(text, id);
    },
    [fallback, scheduleReset]
  );

  return { copied, copy };
};
